<template>
  <div>
    <backdrop-loader :isLoading="loading" />
    <div class="header">
        <h1 style="margin: 0; display: block">
            Edit {{ verticalName }} {{ pipelineLabel }}
            <a :href="formulaEditLink">Formula ID: {{ $store.state.formulaId }}</a>
        </h1>
        <div class="header-links">
            <formula-tips :go-back-url="goBackLink"/>
        </div>
    </div>
    <hr class="solid-separator" />
    <div class="pc-control-section">
      <div>
          <formula-errors :errors="errors" />
          <formula-price :pricing-details="pricingDetails" />
      </div>
      <div>
          <button
            class="btn v2_btn v2_btn-primary v2_btn-transparent"
            v-on:click="onPriceCheckHandler"
            :disabled="loading"
          >
            Check Price
          </button>
          <button
            v-if="!readOnly"
            class="btn v2_btn v2_btn-primary v2_btn-transparent"
            v-on:click="onFormulaSaveHandler"
            :disabled="!isFormulaModified || loading"
          >
            Save Changes
          </button>
          <button
            v-if="!readOnly"
            class="btn v2_btn v2_btn-primary v2_btn-transparent"
            v-on:click="onFormulaDiscardChangesHandler"
            :disabled="!isFormulaModified || loading"
          >
            Discard Changes
          </button>
      </div>
    </div>
    <Split style="height: 70vh">
      <SplitArea :size="35">
        <div class="split-area-container">
          <h2 style="margin-top: 0">Event</h2>
          <event-lookup :event.sync="event" />
          <event-editor :event.sync="event" />
        </div>
      </SplitArea>
      <SplitArea :size="65">
        <div class="split-area-container">
          <div class="formula-editor-title">
            <h2 style="margin-top: 0">Formula</h2>
            <p class="formula-live-caption" v-if="readOnly">{{ readOnlyCaption }}</p>
          </div>
          <formula-rich-editor :formula.sync="formula" />
        </div>
      </SplitArea>
    </Split>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import VueSplit from "vue-split-panel";
import {mapGetters, mapState, mapActions} from "vuex";

import store from "./store";
import FormulaTips from "./shared/tips.vue";
import FormulaPrice from "./shared/price.vue";
import PriceMixin from "./shared/formula.mixin";
import EventLookup from "./shared/event/lookup.vue";
import EventEditor from "./shared/event/editor.vue";
import FormulaErrors from "./shared/header-errors.vue";
import BackdropLoader from "../utils/backdrop-loader.vue";
import FormulaRichEditor from "./shared/formula-rich-editor.vue";

Vue.use(VueSplit);

export default {
  store,
  components: {
    EventLookup,
    EventEditor,
    FormulaTips,
    FormulaPrice,
    FormulaErrors,
    BackdropLoader,
    FormulaRichEditor,
  },
  mixins: [PriceMixin],
  name: "PriceCheck",
  props: {
    formulaId: {
      default: null,
      require: true,
    },
    pipelineSlug: {
      default: null,
      required: true,
    },
    refererUrl: {
      default: null,
      required: false,
    },
  },
  data() {
    return {
      event: "",
      errors: [],
      formula: "",
    };
  },
  methods: {
    ...mapActions(["checkPrice", "fetchFormula", "saveFormula"]),
    onPriceCheckHandler: async function () {
      this.errors = this.validateCheckPriceForm(this.event, this.formula);

      if (this.errors.length > 0) {
        this.$store.commit("setPricingDetails", null);
        return;
      }

      try {
        await this.checkPrice({
            event: this.event,
            formula: this.formula,
            formulaId: this.$store.state.formulaId,
            pipelineSlug: this.$store.state.pipelineSlug,
        });
      } catch (error) {
        if ((error.message = "FormulaExecutionFailure")) {
          this.errors = error.cause;
          return;
        }
      }
    },
    onFormulaSaveHandler: async function () {
      try {
        await this.saveFormula({
          formula: this.formula,
          formulaId: this.$store.state.formulaId,
          pipelineSlug: this.$store.state.pipelineSlug,
        });

        Swal.fire({
          icon: "success",
          title: "Succeed",
          text: "Formula was successfully updated!",
        });
      } catch (error) {
        if (error && error.response && error.response.status === 403) {
          this.fireSimpleErrorAlert(`Formula has gone live and can not longer be edited.`);

          return;
        }

        this.fireSimpleErrorAlert(
          `Unable to save ${this.$store.state.pipelineSlug} Formula ${this.$store.state.formulaId}. Please try again later.`
        );
      }
    },
    onFormulaDiscardChangesHandler: async function () {
      const result = await Swal.fire({
        icon: "warning",
        allowOutsideClick: false,
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, discard changes!",
      });

      if (result.isConfirmed) this.formula = this.formulaEntity.formula;
    },
    async loadFormula() {
      if (this.$store.state.pipelineSlug && this.$store.state.formulaId) {
        await this.fetchFormula({
          formulaId: this.$store.state.formulaId,
          pipelineSlug: this.$store.state.pipelineSlug,
        });

        this.formula = this.formulaEntity.formula;
      }
    },
  },
  computed: {
    ...mapGetters(["variables", "pipelineLabel", "verticalName"]),
    ...mapState(["loading", "formulaEntity", "pricingDetails"]),
    isValid: function () {
      return this.errors.length === 0;
    },
    readOnly: function () {
      if (this.pipelineSlug === "shadow") return false;

      return !this.formulaEntity || this.formulaEntity.has_gone_live;
    },
    readOnlyCaption: function () {
      if (!this.formulaEntity || this.pipelineSlug === "shadow") return "";

      return `Live on ${this.formulaEntity.live_date_at_formatted} and is not longer available for editing.`;
    },
    isFormulaModified: function () {
      if (!this.formulaEntity) return false;
      return this.formulaEntity.formula !== this.formula;
    },
    goBackLink() {
      return this.refererUrl || this.formulaEditLink;
    },
    formulaEditLink() {
      return `/manage/resources/${this.pipelineLabel.toLowerCase()}-formula/${this.$store.state.formulaId}`;
    },
  },
  created() {
    this.$store.commit("setFormulaId", this.formulaId);
    this.$store.commit("setPipelineSlug", this.pipelineSlug);

    this.loadFormula();
  },
};
</script>

<style>
.split .gutter.gutter-horizontal:hover {
  transform: scale(1.15);
  transition: all 0.2s ease;
}
</style>

<style scoped>
.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0 16px;
}
.solid-separator {
  margin-left: 24px;
  margin-right: 24px;
  box-shadow: 0 1em 1em -1em rgba(0, 0, 0, 0.25);
}
.split-area-container {
  display: flex;
  overflow: hidden;
  height: 100%;
  flex-direction: column;
  transition: width 0.5s;
  padding: 0 16px 10px 16px;
  min-width: 640px;
}

.v2_btn-primary {
  border: solid #4e63ff 1px;
}

.v2_btn-transparent {
  background: transparent;
}

.v2_btn {
  cursor: pointer;
}

.v2_btn:hover {
  transform: scale(1.015);
  animation: 500ms ease-in-out forwards;
}
.pc-control-section {
  display: flex;
  justify-content: space-between;
  margin: 0 20px;
  gap: 16px;
}

.formula-editor-title {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.formula-live-caption {
  margin-bottom: 0px;
  font-size: 16px;
  padding-top: 12px;
  color: #d7a100;
}

.header-links {
    display: flex;
    justify-content: space-between;
}

.header-links a {
    padding-top: 7px;
}
</style>
